import React, { useState } from "react";
import {
  X,
  ThumbsUp,
  ThumbsDown,
  CheckCircle2,
  Send,
  Sparkles,
} from "lucide-react";
import { addDoc, collection } from "firebase/firestore";

const ISSUES = [
  { id: "not_relevant", label: "Didn't match my mood or preferences" },
  { id: "already_watched", label: "I've already watched most of these" },
  { id: "too_mainstream", label: "Felt too mainstream or generic" },
  { id: "wrong_genre", label: "Wrong genre or content type" },
  { id: "bad_quality", label: "Low-quality or obscure titles" },
  { id: "missing_platform", label: "Titles not on my streaming platform" },
];

const FeedbackModal = ({
  isOpen,
  onClose,
  user,
  isPremium,
  db,
  sessionIndex,
}) => {
  const [phase, setPhase] = useState("ask");
  const [selected, setSelected] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const toggleIssue = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );

  // ── THE KEY FIX: saves for EVERYONE, not just logged-in users ──
  const saveFeedback = async (liked, issues = [], other = "") => {
    const payload = {
      liked,
      issues,
      otherText: other.trim(),
      sessionIndex,
      timestamp: new Date().toISOString(),
      userId: user?.uid || null,
      userEmail: user?.email || "guest",
      isPremium: !!isPremium,
    };

    // localStorage fallback (always)
    try {
      const existing = JSON.parse(
        localStorage.getItem("noryq-feedback") || "[]",
      );
      existing.push(payload);
      localStorage.setItem("noryq-feedback", JSON.stringify(existing));
    } catch (_) {}

    // Firestore — for ALL users including guests (rules: allow create: if true)
    if (db) {
      try {
        await addDoc(collection(db, "feedback"), payload);
        console.log("✅ Feedback saved to Firestore");
      } catch (err) {
        console.error("❌ Firestore feedback error:", err);
      }
    }
  };

  const handleLiked = async () => {
    await saveFeedback(true);
    setPhase("thanks");
  };

  const handleSubmitForm = async () => {
    if (selected.length === 0 && otherText.trim() === "") return;
    setIsSubmitting(true);
    await saveFeedback(false, selected, otherText);
    setIsSubmitting(false);
    setPhase("thanks");
  };

  const handleClose = () => {
    setPhase("ask");
    setSelected([]);
    setOtherText("");
    onClose();
  };

  // ── Style tokens ────────────────────────────────────────
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(8,8,24,0.82)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "1rem",
  };

  const card = {
    background: "linear-gradient(160deg, #1c1c42 0%, #141430 100%)",
    border: "1px solid rgba(102,126,234,0.3)",
    borderRadius: "28px",
    maxWidth: "500px",
    width: "100%",
    position: "relative",
    boxShadow: "0 30px 90px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
    fontFamily: '"DM Sans", sans-serif',
    color: "#fff",
    maxHeight: "92vh",
    overflowY: "auto",
    overflowX: "hidden",
  };

  const closeBtn = {
    position: "absolute",
    top: "1.1rem",
    right: "1.1rem",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "50%",
    width: "34px",
    height: "34px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#fff",
    zIndex: 2,
  };

  const primaryBtn = {
    flex: 1,
    padding: "1rem 1.5rem",
    borderRadius: "16px",
    border: "none",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    color: "#fff",
    fontWeight: "700",
    fontSize: "1rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    boxShadow: "0 8px 24px rgba(245,87,108,0.35)",
    transition: "opacity 0.2s",
    fontFamily: '"DM Sans", sans-serif',
  };

  const secondaryBtn = {
    flex: 1,
    padding: "1rem 1.5rem",
    borderRadius: "16px",
    border: "1.5px solid rgba(255,255,255,0.15)",
    background: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.85)",
    fontWeight: "600",
    fontSize: "1rem",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    transition: "background 0.2s",
    fontFamily: '"DM Sans", sans-serif',
  };

  const issueChip = (active) => ({
    padding: "0.8rem 1rem",
    borderRadius: "14px",
    border: active
      ? "1.5px solid rgba(240,147,251,0.65)"
      : "1.5px solid rgba(255,255,255,0.1)",
    background: active ? "rgba(240,147,251,0.1)" : "rgba(255,255,255,0.04)",
    color: active ? "#f5c6ff" : "rgba(255,255,255,0.75)",
    fontWeight: active ? "600" : "400",
    fontSize: "0.875rem",
    cursor: "pointer",
    transition: "all 0.18s",
    textAlign: "left",
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    fontFamily: '"DM Sans", sans-serif',
  });

  const SparkleOrb = ({ size, top, left, right, opacity = 0.2 }) => (
    <div
      style={{
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        background: `rgba(255,255,255,${opacity})`,
        top,
        left,
        right,
        pointerEvents: "none",
      }}
    />
  );

  // ── PHASE: ASK ─────────────────────────────────────────────────
  if (phase === "ask") {
    return (
      <div style={overlay}>
        <div style={card}>
          <button style={closeBtn} onClick={handleClose}>
            <X size={16} />
          </button>

          {/* Hero gradient header */}
          <div
            style={{
              background:
                "linear-gradient(135deg, #667eea 0%, #764ba2 55%, #f5576c 100%)",
              borderRadius: "28px 28px 0 0",
              padding: "2.5rem 2rem 2rem",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <SparkleOrb size="60px" top="10%" left="5%" opacity={0.15} />
            <SparkleOrb size="35px" top="55%" left="80%" opacity={0.12} />
            <SparkleOrb size="20px" top="20%" left="70%" opacity={0.2} />
            <SparkleOrb size="45px" top="65%" left="10%" opacity={0.1} />

            <div
              style={{
                width: "68px",
                height: "68px",
                borderRadius: "20px",
                background: "rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                border: "1.5px solid rgba(255,255,255,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.25rem",
                fontSize: "2.2rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              🎬
            </div>
            <h2
              style={{
                fontSize: "1.65rem",
                fontWeight: "800",
                margin: "0 0 0.5rem",
                letterSpacing: "-0.3px",
                position: "relative",
                zIndex: 1,
              }}
            >
              How were your picks?
            </h2>
            <p
              style={{
                opacity: 0.8,
                fontSize: "0.9rem",
                margin: 0,
                lineHeight: 1.5,
                position: "relative",
                zIndex: 1,
              }}
            >
              Quick feedback helps us nail your next recommendations
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ padding: "1.75rem", display: "flex", gap: "0.875rem" }}>
            <button
              style={secondaryBtn}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.11)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
              }
              onClick={() => setPhase("form")}
            >
              <ThumbsDown size={18} /> Not Really
            </button>
            <button
              style={primaryBtn}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              onClick={handleLiked}
            >
              <ThumbsUp size={18} /> Loved It!
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── PHASE: IMPROVEMENT FORM ─────────────────────────────────────
  if (phase === "form") {
    return (
      <div style={overlay}>
        <div style={card}>
          <button style={closeBtn} onClick={handleClose}>
            <X size={16} />
          </button>

          {/* Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "28px 28px 0 0",
              padding: "2rem 2rem 1.75rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <SparkleOrb size="50px" top="15%" left="80%" opacity={0.15} />
            <SparkleOrb size="30px" top="60%" left="5%" opacity={0.12} />
            <div
              style={{
                fontSize: "1.75rem",
                marginBottom: "0.6rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              🛠️
            </div>
            <h2
              style={{
                fontSize: "1.4rem",
                fontWeight: "800",
                margin: "0 0 0.3rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              Help Us Improve
            </h2>
            <p
              style={{
                opacity: 0.75,
                fontSize: "0.85rem",
                margin: 0,
                position: "relative",
                zIndex: 1,
              }}
            >
              What went wrong? Select all that apply
            </p>
          </div>

          <div style={{ padding: "1.5rem" }}>
            {/* MCQ chips */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
                marginBottom: "1.25rem",
              }}
            >
              {ISSUES.map((issue) => {
                const active = selected.includes(issue.id);
                return (
                  <button
                    key={issue.id}
                    style={issueChip(active)}
                    onClick={() => toggleIssue(issue.id)}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "6px",
                        flexShrink: 0,
                        border: active
                          ? "none"
                          : "2px solid rgba(255,255,255,0.25)",
                        background: active
                          ? "linear-gradient(135deg,#f093fb,#f5576c)"
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.18s",
                      }}
                    >
                      {active && (
                        <svg
                          width="11"
                          height="9"
                          viewBox="0 0 11 9"
                          fill="none"
                        >
                          <path
                            d="M1 4L4 7.5L10 1"
                            stroke="white"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    {issue.label}
                  </button>
                );
              })}
            </div>

            {/* Other */}
            <div style={{ marginBottom: "1.5rem" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.78rem",
                  fontWeight: "700",
                  marginBottom: "0.5rem",
                  opacity: 0.55,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Anything else?
              </label>
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="e.g. I wanted more anime, or something lighter..."
                rows={3}
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.05)",
                  border: "1.5px solid rgba(102,126,234,0.3)",
                  borderRadius: "14px",
                  padding: "0.875rem 1rem",
                  color: "#fff",
                  fontSize: "0.875rem",
                  resize: "vertical",
                  outline: "none",
                  fontFamily: '"DM Sans", sans-serif',
                  transition: "border-color 0.2s",
                  boxSizing: "border-box",
                  lineHeight: 1.5,
                }}
                onFocus={(e) =>
                  (e.target.style.borderColor = "rgba(240,147,251,0.55)")
                }
                onBlur={(e) =>
                  (e.target.style.borderColor = "rgba(102,126,234,0.3)")
                }
              />
            </div>

            <div style={{ display: "flex", gap: "0.75rem" }}>
              <button
                style={{
                  ...secondaryBtn,
                  flex: "0 0 auto",
                  padding: "0.9rem 1.25rem",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
                }
                onClick={handleClose}
              >
                Skip
              </button>
              <button
                style={{
                  ...primaryBtn,
                  opacity:
                    selected.length === 0 && !otherText.trim() ? 0.45 : 1,
                  cursor:
                    selected.length === 0 && !otherText.trim()
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={handleSubmitForm}
                disabled={
                  isSubmitting || (selected.length === 0 && !otherText.trim())
                }
              >
                {isSubmitting ? (
                  "Saving..."
                ) : (
                  <>
                    <Send size={15} /> Submit Feedback
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PHASE: THANKS ───────────────────────────────────────────────
  if (phase === "thanks") {
    return (
      <div style={overlay}>
        <div style={{ ...card, textAlign: "center" }}>
          <div
            style={{
              background:
                "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f5576c 100%)",
              borderRadius: "28px 28px 0 0",
              padding: "2.75rem 2rem 2.25rem",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <SparkleOrb size="55px" top="10%" left="5%" opacity={0.15} />
            <SparkleOrb size="30px" top="60%" left="80%" opacity={0.12} />
            <SparkleOrb size="20px" top="25%" left="72%" opacity={0.2} />

            <div
              style={{
                width: "76px",
                height: "76px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(255,255,255,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.25rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              <CheckCircle2 size={36} />
            </div>
            <h2
              style={{
                fontSize: "1.7rem",
                fontWeight: "800",
                margin: "0 0 0.4rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              You're awesome! 🎉
            </h2>
            <p
              style={{
                opacity: 0.85,
                fontSize: "0.9rem",
                margin: 0,
                position: "relative",
                zIndex: 1,
              }}
            >
              Your feedback is now shaping NORYQ's AI
            </p>
          </div>

          <div style={{ padding: "1.75rem" }}>
            <p
              style={{
                opacity: 0.6,
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
                lineHeight: 1.6,
              }}
            >
              Every response helps our AI understand what people actually enjoy.
              Thank you for making NORYQ smarter for everyone. ✨
            </p>
            <button
              style={{ ...primaryBtn, width: "100%" }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              onClick={handleClose}
            >
              <Sparkles size={16} /> Keep Exploring
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FeedbackModal;
